import * as SecureStore from 'expo-secure-store';

// Issue #125: Supabase's own session persistence previously went
// straight to AsyncStorage (lib/supabase.ts) -- unencrypted, readable
// by anything with filesystem access to the app's sandbox (a rooted/
// jailbroken device, a stolen or restored backup). expo-secure-store
// uses the platform keystore (Android Keystore / iOS Keychain), the
// real fix for "a compromised sandbox or stolen backup yields a live
// session."
//
// Chunked, not a direct pass-through -- SecureStore enforces a hard
// ~2048-byte-per-item limit (the platform keystore APIs it wraps are
// designed for small secrets, not arbitrary blobs), but a Supabase
// session (access token + refresh token + user metadata, JSON-
// serialized) routinely exceeds that. This is the standard Supabase-
// documented recipe for exactly this mismatch: split anything over the
// limit across multiple keystore entries (`${key}_0`, `${key}_1`, ...),
// with a small JSON envelope under the original key recording how many
// chunks to reassemble. A value that already fits under the limit
// skips chunking entirely -- no envelope, no extra keystore entries,
// stored exactly as a direct SecureStore call would.
const CHUNK_SIZE = 2000; // stays under SecureStore's ~2048-byte ceiling with margin for the envelope itself
const CHUNK_ENVELOPE_MARKER = '__chunked__';

interface ChunkEnvelope {
  marker: typeof CHUNK_ENVELOPE_MARKER;
  chunkCount: number;
}

function isChunkEnvelope(value: string): ChunkEnvelope | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'marker' in parsed &&
      (parsed as { marker: unknown }).marker === CHUNK_ENVELOPE_MARKER
    ) {
      return parsed as ChunkEnvelope;
    }
  } catch {
    // Not JSON at all -- a plain, unchunked stored value. Not an error;
    // every value written before this adapter existed (or any value
    // under CHUNK_SIZE) looks like this.
  }

  return null;
}

function chunkKey(key: string, index: number): string {
  return `${key}_${index}`;
}

async function removeAllChunks(key: string, chunkCount: number): Promise<void> {
  await Promise.all(
    Array.from({ length: chunkCount }, (_, i) => SecureStore.deleteItemAsync(chunkKey(key, i)).catch(() => {})),
  );
}

// Matches the shape supabase-js's `storage` option expects (async
// getItem/setItem/removeItem) -- see lib/supabase.ts's own wiring.
export const chunkedSecureStorageAdapter = {
  async getItem(key: string): Promise<string | null> {
    const value = await SecureStore.getItemAsync(key);
    if (value === null) return null;

    const envelope = isChunkEnvelope(value);
    if (!envelope) return value;

    const chunks = await Promise.all(
      Array.from({ length: envelope.chunkCount }, (_, i) => SecureStore.getItemAsync(chunkKey(key, i))),
    );

    // A missing chunk means the stored value can't be reassembled
    // correctly -- treating it as "no session" (rather than returning
    // a corrupt, partial string) is the safe failure mode: Supabase
    // then just falls back to signed-out, the same as no session ever
    // having been stored, not a crash or a garbled auth state.
    if (chunks.some((chunk) => chunk === null)) return null;

    return chunks.join('');
  },

  async setItem(key: string, value: string): Promise<void> {
    if (value.length <= CHUNK_SIZE) {
      // A previous, larger value under this same key may have left
      // chunk entries behind -- clean those up before writing the
      // now-short value directly, or they'd become orphaned, unreadable
      // (never referenced by anything) SecureStore entries.
      const existing = await SecureStore.getItemAsync(key);
      const existingEnvelope = existing !== null ? isChunkEnvelope(existing) : null;
      if (existingEnvelope) {
        await removeAllChunks(key, existingEnvelope.chunkCount);
      }

      await SecureStore.setItemAsync(key, value);
      return;
    }

    const chunkCount = Math.ceil(value.length / CHUNK_SIZE);
    await Promise.all(
      Array.from({ length: chunkCount }, (_, i) =>
        SecureStore.setItemAsync(chunkKey(key, i), value.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)),
      ),
    );

    const envelope: ChunkEnvelope = { marker: CHUNK_ENVELOPE_MARKER, chunkCount };
    await SecureStore.setItemAsync(key, JSON.stringify(envelope));
  },

  async removeItem(key: string): Promise<void> {
    const value = await SecureStore.getItemAsync(key);
    const envelope = value !== null ? isChunkEnvelope(value) : null;

    if (envelope) {
      await removeAllChunks(key, envelope.chunkCount);
    }

    await SecureStore.deleteItemAsync(key);
  },
};
